/**
 * Decode URL-encoded parameters
 * @param {string} param - URL-encoded parameter
 * @returns {string|null} Decoded parameter or null if empty
 */
export const decodeParam = (param) => {
  if (!param) return null;
  return decodeURIComponent(param);
};

/**
 * Normalize round number to "Round X" format
 * Handles various input formats: "round1", "Round 1", "1", etc.
 * @param {string} roundParam - Round parameter from URL
 * @returns {string} Normalized round string in "Round X" format
 */
export const normalizeRoundNumber = (roundParam) => {
  if (!roundParam) return "";

  // Remove "Round" prefix if present and extract number
  const roundStr = roundParam.replace(/^round\s*/i, "").trim();

  // Extract numeric part
  const match = roundStr.match(/\d+/);
  if (!match) return "";

  const number = match[0];
  return `Round ${number}`;
};

/**
 * Reconstruct event string from URL parameters
 * Format: {event}-{level}-Round {number}
 * Examples:
 * - "SCORE-Prelims-Round 1"
 * - "Revision Test-Prelims-Round 1"
 * - "SCORE Edge Scholarship Test-Prelims-Round 4"
 *
 * @param {Object} params - Event parameters
 * @param {string} params.event - Event name
 * @param {string} [params.level] - Event level (e.g., "Prelims", "Semi", "Finals")
 * @param {string} [params.round] - Round number (e.g., "round1", "1", "Round 1")
 * @returns {string|null} Reconstructed event string or null if event is missing
 */
export const reconstructEventString = ({ event, level, round }) => {
  if (!event) return null;

  const decodedEvent = decodeParam(event);
  const decodedLevel = decodeParam(level);
  const decodedRound = decodeParam(round);

  // Start with event name
  let eventString = decodedEvent;

  // Add level if available (format: {event}-{level})
  if (decodedLevel) {
    eventString += `-${decodedLevel}`;
  }

  // Add round number if available (format: -Round {number})
  if (decodedRound) {
    const normalizedRound = normalizeRoundNumber(decodedRound);
    if (normalizedRound) {
      eventString += `-${normalizedRound}`;
    }
  }

  return eventString;
};
